import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listPendingAppeals, reviewAppeal } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/appeals")({
  head: () => ({ meta: [{ title: "Appeal Reviews — Admin" }] }),
  component: AdminAppealsPage,
});

type Appeal = {
  id: string;
  status: string;
  description: string;
  evidence_path: string | null;
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  user_id: string;
  strike_id: string;
  user_strikes: {
    id: string;
    strike_number: number;
    reason: string;
    status: string;
    audit_log_id: string | null;
    created_at: string;
  };
};
type Profile = { id: string; full_name: string | null; role: string; is_suspended: boolean };
type Audit = {
  id: string; shift_id: string; reason: string; reason_text: string | null;
  hours_before_start: number | null; cancelled_by_role: string; created_at: string;
};

function AdminAppealsPage() {
  const fetchAppeals = useServerFn(listPendingAppeals);
  const review = useServerFn(reviewAppeal);

  const [data, setData] = useState<{ appeals: Appeal[]; profiles: Profile[]; audits: Audit[]; signed: Record<string, string> } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<Appeal | null>(null);
  const [decision, setDecision] = useState<"approved" | "denied">("approved");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState<"pending" | "all">("pending");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetchAppeals();
      setData(r as typeof data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load appeals.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <ReserveHeader />
        <main className="mx-auto max-w-2xl px-6 py-16 text-center">
          <h1 className="font-display text-2xl mb-2">Access denied</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </main>
      </div>
    );
  }

  const profileMap = new Map((data?.profiles ?? []).map((p) => [p.id, p]));
  const auditMap = new Map((data?.audits ?? []).map((a) => [a.id, a]));
  const appeals = (data?.appeals ?? []).filter((a) => filter === "all" || a.status === "pending");

  const submit = async () => {
    if (!active) return;
    setSubmitting(true);
    try {
      const r = await review({ data: { appeal_id: active.id, decision, admin_notes: notes.trim() || undefined } });
      if ("error" in r) throw new Error(r.error);
      toast.success(decision === "approved" ? "Appeal approved — strike overturned." : "Appeal denied.");
      setActive(null); setNotes(""); setDecision("approved");
      await load();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not submit."); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="tracking-wide-luxury text-accent">Admin</p>
            <h1 className="mt-3 font-display text-3xl">Cancellation Appeals</h1>
            <span className="gold-divider mt-3 block" />
          </div>
          <div className="flex gap-2">
            <Button variant={filter === "pending" ? "default" : "outline"}
              className="rounded-none tracking-wide-luxury text-xs"
              onClick={() => setFilter("pending")}>Pending</Button>
            <Button variant={filter === "all" ? "default" : "outline"}
              className="rounded-none tracking-wide-luxury text-xs"
              onClick={() => setFilter("all")}>All</Button>
          </div>
        </div>

        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && appeals.length === 0 && (
          <p className="text-sm text-muted-foreground">No appeals to review.</p>
        )}

        <ul className="space-y-4">
          {appeals.map((a) => {
            const profile = profileMap.get(a.user_id);
            const audit = a.user_strikes.audit_log_id ? auditMap.get(a.user_strikes.audit_log_id) : null;
            const signed = a.evidence_path ? data?.signed[a.evidence_path] : null;
            return (
              <li key={a.id} className="border border-border p-5 space-y-3">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <div className="text-sm font-medium">
                      {profile?.full_name || "Unknown member"}
                      <span className="ml-2 text-xs opacity-70 uppercase tracking-wide-luxury">{profile?.role}</span>
                      {profile?.is_suspended && (
                        <span className="ml-2 text-xs text-destructive uppercase tracking-wide-luxury">Suspended</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Submitted {new Date(a.created_at).toLocaleString()}
                    </div>
                  </div>
                  <span className={`text-xs uppercase tracking-wide-luxury px-2 py-1 border ${
                    a.status === "pending" ? "border-accent text-accent"
                    : a.status === "approved" ? "border-green-600 text-green-600"
                    : "border-destructive text-destructive"
                  }`}>{a.status}</span>
                </div>

                <div className="text-xs text-muted-foreground">
                  Strike {a.user_strikes.strike_number} · {a.user_strikes.reason}
                </div>

                {audit && (
                  <div className="text-xs border-l-2 border-border pl-3 space-y-0.5">
                    <div><span className="opacity-60">Reason given:</span> {audit.reason}{audit.reason_text ? ` — ${audit.reason_text}` : ""}</div>
                    <div><span className="opacity-60">Hours before start:</span> {audit.hours_before_start?.toFixed(1)}</div>
                    <div><span className="opacity-60">Cancelled by:</span> {audit.cancelled_by_role}</div>
                  </div>
                )}

                <div>
                  <div className="text-xs uppercase tracking-wide-luxury text-muted-foreground mb-1">Member's statement</div>
                  <p className="text-sm whitespace-pre-wrap">{a.description}</p>
                </div>

                {signed && (
                  <a href={signed} target="_blank" rel="noreferrer"
                    className="text-xs underline tracking-wide-luxury">
                    View evidence ↗
                  </a>
                )}

                {a.status === "pending" ? (
                  <div className="flex gap-2 pt-2">
                    <Button onClick={() => { setActive(a); setDecision("approved"); setNotes(""); }}
                      className="rounded-none tracking-wide-luxury text-xs">Approve</Button>
                    <Button variant="outline" onClick={() => { setActive(a); setDecision("denied"); setNotes(""); }}
                      className="rounded-none border-border tracking-wide-luxury text-xs">Deny</Button>
                  </div>
                ) : (
                  <div className="text-xs border-t border-border pt-2">
                    <div className="opacity-70">Reviewed {a.reviewed_at ? new Date(a.reviewed_at).toLocaleString() : ""}</div>
                    {a.admin_notes && <div className="mt-1 italic">{a.admin_notes}</div>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </main>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{decision === "approved" ? "Approve appeal" : "Deny appeal"}</DialogTitle>
            <DialogDescription>
              {decision === "approved"
                ? "The strike will be overturned and the account un-suspended if the user is below 3 active strikes."
                : "The strike remains on the user's account. They will be notified."}
            </DialogDescription>
          </DialogHeader>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} maxLength={2000}
            placeholder="Notes for the audit log (visible to the user)" className="rounded-none" />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setActive(null)}
              className="rounded-none border-border tracking-wide-luxury text-xs">Cancel</Button>
            <Button onClick={submit} disabled={submitting}
              className="rounded-none tracking-wide-luxury text-xs">
              {submitting ? "Submitting…" : `Confirm ${decision === "approved" ? "approval" : "denial"}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
